/**
 * Lingua dell'app (Andrea 2026-10-09): italiano di riferimento + inglese.
 *
 * Scelta pratica: le frasi italiane scritte nel codice SONO le chiavi
 * (`t("Salva formazione")`), e `en.json` e' una tabella italiano → inglese.
 * Cosi' il codice resta leggibile, non si inventano chiavi, e dove manca
 * la traduzione esce l'italiano, mai una chiave vuota.
 *
 * La scelta vive in localStorage ("fs-lingua") e sul profilo utente
 * (`users.lingua`, cosi' un giorno notifiche ed email la seguiranno).
 */

import { useSyncExternalStore } from "react";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";

export type Lingua = "it" | "en";
export const LINGUE: { id: Lingua; nome: string; bandiera: string }[] = [
  { id: "it", nome: "Italiano", bandiera: "🇮🇹" },
  { id: "en", nome: "English", bandiera: "🇬🇧" },
];
export const CHIAVE_LINGUA = "fs-lingua";

function leggiLingua(): Lingua {
  try {
    const v = localStorage.getItem(CHIAVE_LINGUA);
    if (v === "en" || v === "it") return v;
  } catch {
    /* localStorage non disponibile */
  }
  return "it";
}

void i18next.use(initReactI18next).init({
  resources: { it: { translation: {} }, en: { translation: en } },
  lng: leggiLingua(),
  fallbackLng: "it",
  // le chiavi sono frasi intere: niente separatori su "." e ":"
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

export function linguaCorrente(): Lingua {
  return i18next.language === "en" ? "en" : "it";
}

/** Locale per Intl (date, numeri). */
export function localeCorrente(): string {
  return linguaCorrente() === "en" ? "en-GB" : "it-IT";
}

export function impostaLingua(l: Lingua, salva = true) {
  if (salva) {
    try {
      localStorage.setItem(CHIAVE_LINGUA, l);
    } catch {
      /* ignora */
    }
  }
  document.documentElement.lang = l;
  if (i18next.language !== l) {
    // Le etichette negli array e i tr() di modulo sono valutati una volta:
    // al cambio di lingua si ricarica la pagina (capita di rado).
    void i18next.changeLanguage(l).then(() => window.location.reload());
  }
}

/** Traduzione fuori dai componenti (etichette, formattatori). */
export function tr(frase: string, opzioni?: Record<string, unknown>): string {
  return i18next.t(frase, opzioni) as string;
}

document.documentElement.lang = linguaCorrente();
export default i18next;

/** La lingua corrente come stato React: l'app si rimonta quando cambia,
 *  cosi' anche chi usa `tr()` di modulo (fuori dagli hook) si aggiorna. */
export function useLingua(): Lingua {
  return useSyncExternalStore(
    (cb) => {
      i18next.on("languageChanged", cb);
      return () => i18next.off("languageChanged", cb);
    },
    linguaCorrente,
    () => "it" as Lingua,
  );
}
